import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import Map, { Marker, Popup, NavigationControl } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import axios from "axios";
import { API } from "../../../contexts/AuthContext";
import { Crosshair, Share2, Map as MapIcon } from "lucide-react";
import { DayLegend } from "./DayLegend";
import { ActivityPopup } from "./ActivityPopup";

const LORA = { fontFamily: "'Lora', Georgia, serif" };
const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const STADIA_KEY = process.env.REACT_APP_STADIA_MAPS_API_KEY || "";

// Style raster MapLibre : Alidade Smooth (labels inclus dans le style de base)
function buildMapStyle() {
  const suffix = STADIA_KEY ? `?api_key=${STADIA_KEY}` : "";
  // Retina : MapLibre ne substitue pas le token {r} ici → on insère @2x nous-mêmes
  const r = (typeof window !== "undefined" && window.devicePixelRatio > 1) ? "@2x" : "";
  return {
    version: 8,
    sources: {
      basemap: {
        type: "raster",
        tiles: [`https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}${r}.png${suffix}`],
        tileSize: 256,
        attribution:
          '© Stadia Maps © OpenMapTiles © OpenStreetMap contributors',
      },
    },
    layers: [
      { id: "basemap", type: "raster", source: "basemap" },
    ],
  };
}

export const TripMapTab = ({ tripId, trip }) => {
  const [status, setStatus] = useState("loading");
  const [mapData, setMapData] = useState(null);
  const [activeDay, setActiveDay] = useState(null);
  const [activePoint, setActivePoint] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapRef = useRef(null);
  const mapWrapRef = useRef(null);
  const pollRef = useRef(null);

  const mapStyle = useMemo(() => buildMapStyle(), []);

  const fetchMap = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/trips/${tripId}/map`);
      if (data.offline) {
        setStatus((s) => (s === "completed" ? s : "offline"));
        clearInterval(pollRef.current);
        return;
      }
      setStatus(data.status);
      if (data.status === "completed") {
        setMapData(data);
        clearInterval(pollRef.current);
      } else if (data.status !== "pending") {
        clearInterval(pollRef.current); // failed / not_available → on arrête
      }
    } catch {
      // Erreur réseau : si hors-ligne on l'indique, sinon on continue de poller
      if (!navigator.onLine) {
        setStatus((s) => (s === "completed" ? s : "offline"));
        clearInterval(pollRef.current);
      }
    }
  }, [tripId]);

  useEffect(() => {
    let cancelled = false;
    const poll = () => { if (!cancelled) fetchMap(); };
    poll();
    pollRef.current = setInterval(poll, 5000);
    return () => { cancelled = true; clearInterval(pollRef.current); };
  }, [fetchMap]);

  // Tous les points géolocalisés (avec couleur + numéro de jour)
  const allPoints = useMemo(() => {
    if (!mapData?.jours) return [];
    const pts = [];
    mapData.jours.forEach((j) => {
      (j.points || []).forEach((p) => {
        if (p.coordinates) {
          pts.push({ ...p, jourNumero: j.numero_jour, couleur: j.couleur });
        }
      });
    });
    return pts;
  }, [mapData]);

  // Lignes de trajet (GeoJSON) — une LineString par jour
  const lineData = useMemo(() => {
    if (!mapData?.jours) return { type: "FeatureCollection", features: [] };
    const features = [];
    mapData.jours.forEach((j) => {
      if (activeDay !== null && j.numero_jour !== activeDay) return;
      const coords = (j.points || [])
        .filter((p) => p.coordinates)
        .map((p) => [p.coordinates.lng, p.coordinates.lat]);
      if (coords.length >= 2) {
        features.push({
          type: "Feature",
          properties: { color: j.couleur },
          geometry: { type: "LineString", coordinates: coords },
        });
      }
    });
    return { type: "FeatureCollection", features };
  }, [mapData, activeDay]);

  const fitToBounds = useCallback(() => {
    const b = mapData?.bounds;
    if (!b || !mapRef.current) return;
    mapRef.current.fitBounds(
      [[b.min_lng, b.min_lat], [b.max_lng, b.max_lat]],
      { padding: 50, duration: 600, maxZoom: 15 }
    );
  }, [mapData]);

  const onMapLoad = useCallback(() => {
    setMapLoaded(true);
    fitToBounds();
  }, [fitToBounds]);

  // Lignes de trajet rendues IMPÉRATIVEMENT (évite que react-map-gl ne
  // transmette des meta-props JSX du preview à la définition de source MapLibre).
  useEffect(() => {
    const map = mapRef.current?.getMap?.();
    if (!map || !mapLoaded) return;
    const src = map.getSource("routes-src");
    if (src) {
      src.setData(lineData);
    } else {
      map.addSource("routes-src", { type: "geojson", data: lineData });
      map.addLayer({
        id: "routes",
        type: "line",
        source: "routes-src",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": ["get", "color"], "line-width": 3, "line-opacity": 0.85 },
      });
    }
  }, [lineData, mapLoaded]);

  const handleShare = useCallback(async () => {
    const map = mapRef.current?.getMap?.();
    if (!map || sharing) return;
    setSharing(true);
    try {
      // Force un rendu frais puis capture le canvas WebGL de la carte
      // (preserveDrawingBuffer activé). html2canvas ne sait pas capturer le WebGL.
      map.triggerRepaint?.();
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      const mapCanvas = map.getCanvas();
      const dpr = window.devicePixelRatio || 1;
      const out = document.createElement("canvas");
      out.width = mapCanvas.width;
      out.height = mapCanvas.height;
      const ctx = out.getContext("2d");
      ctx.drawImage(mapCanvas, 0, 0);

      // Dessine les marqueurs numérotés par-dessus (overlays DOM non capturés)
      allPoints.forEach((p) => {
        if (activeDay !== null && p.jourNumero !== activeDay) return;
        const pt = map.project([p.coordinates.lng, p.coordinates.lat]);
        const x = pt.x * dpr;
        const y = pt.y * dpr;
        const r = 16 * dpr;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fillStyle = p.couleur;
        ctx.fill();
        ctx.lineWidth = 2 * dpr;
        ctx.strokeStyle = "#FFFFFF";
        ctx.stroke();
        ctx.fillStyle = "#FFFFFF";
        ctx.font = `bold ${13 * dpr}px 'Lora', Georgia, serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(String(p.ordre), x, y);
      });

      const destSlug = (trip?.destination || "voyage")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      const fileName = `mon-voyage-${destSlug}.png`;
      out.toBlob(async (blob) => {
        if (!blob) return;
        const file = new File([blob], fileName, { type: "image/png" });
        if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({ files: [file], title: "Mon itinéraire Voyage-r" });
          } catch { /* annulé par l'utilisateur */ }
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = fileName;
          a.click();
          URL.revokeObjectURL(url);
        }
      }, "image/png");
    } catch {
      /* capture impossible */
    } finally {
      setSharing(false);
    }
  }, [sharing, trip, allPoints, activeDay]);

  const initialView = useMemo(() => {
    const b = mapData?.bounds;
    if (b) {
      return {
        longitude: (b.min_lng + b.max_lng) / 2,
        latitude: (b.min_lat + b.max_lat) / 2,
        zoom: 11,
      };
    }
    return { longitude: 2.35, latitude: 48.85, zoom: 3 };
  }, [mapData]);

  // ── États non-completed ─────────────────────────────────────────────
  if (status === "loading" || status === "pending") {
    return (
      <div data-testid="map-loading">
        <div className="voyage-map-skeleton">
          <div className="voyage-shimmer" />
          <div className="relative z-10 text-center px-4">
            <MapIcon size={28} strokeWidth={1.4} className="text-[#C17F59] mx-auto mb-3" />
            <p className="text-sm text-[#9A8F86] italic" style={LORA}>
              Votre carte est en cours de préparation... 🗺️
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <p className="text-sm text-[#9A8F86] italic py-12 text-center" style={LORA} data-testid="map-failed">
        La carte n&apos;a pas pu être générée pour ce voyage.
      </p>
    );
  }

  if (status === "not_available") {
    return (
      <p className="text-sm text-[#9A8F86] italic py-12 text-center" style={LORA} data-testid="map-not-available">
        La carte sera disponible après validation et paiement.
      </p>
    );
  }

  if (status === "offline") {
    return (
      <p className="text-sm text-[#9A8F86] italic py-12 text-center" style={LORA} data-testid="map-offline">
        La carte interactive nécessite une connexion internet (chargement des tuiles cartographiques).
      </p>
    );
  }

  // ── completed mais aucun point géolocalisé ──────────────────────────
  if (!allPoints.length) {
    return (
      <p className="text-sm text-[#9A8F86] italic py-12 text-center" style={LORA} data-testid="map-empty">
        Aucun lieu n&apos;a pu être localisé sur la carte pour ce voyage.
      </p>
    );
  }

  const duree = mapData.jours.length;

  return (
    <div data-testid="trip-map-tab">
      {/* Header */}
      <div className="mb-4">
        <h2 className="text-2xl sm:text-3xl text-[#2B2623]" style={{ ...SERIF, fontWeight: 600 }}>
          Votre Itinéraire
        </h2>
        <p className="text-sm text-[#9A8F86] italic mb-3" style={LORA}>
          {trip?.destination} · {duree} jour{duree > 1 ? "s" : ""}
        </p>
        <DayLegend jours={mapData.jours} activeDay={activeDay} onSelectDay={setActiveDay} />
      </div>

      {/* Carte */}
      <div
        ref={mapWrapRef}
        className="relative rounded-lg overflow-hidden border border-[#DCD4C6]"
        style={{ height: "max(65vh, 400px)", background: "#F9F6F0" }}
        data-testid="map-container"
      >
        <Map
          ref={mapRef}
          initialViewState={initialView}
          mapStyle={mapStyle}
          style={{ width: "100%", height: "100%" }}
          onLoad={onMapLoad}
          onClick={() => setActivePoint(null)}
          preserveDrawingBuffer={true}
          attributionControl={false}
        >
          <NavigationControl position="bottom-right" showCompass={false} />

          {/* Marqueurs numérotés */}
          {allPoints.map((p) => {
            const dimmed = activeDay !== null && p.jourNumero !== activeDay;
            const isActive =
              activePoint &&
              activePoint.activite_id === p.activite_id &&
              activePoint.jourNumero === p.jourNumero;
            return (
              <Marker
                key={`${p.jourNumero}-${p.activite_id}-${p.ordre}`}
                longitude={p.coordinates.lng}
                latitude={p.coordinates.lat}
                anchor="center"
                onClick={(e) => {
                  e.originalEvent.stopPropagation();
                  setActivePoint(p);
                }}
              >
                <div
                  className="voyage-map-marker"
                  style={{
                    width: isActive ? 40 : 32,
                    height: isActive ? 40 : 32,
                    backgroundColor: p.couleur,
                    border: isActive ? "3px solid #C17F59" : "3px solid #FFFFFF",
                    opacity: dimmed ? 0.2 : 1,
                    transform: isActive ? "scale(1.2)" : "scale(1)",
                    zIndex: isActive ? 10 : 1,
                    boxShadow: isActive
                      ? `0 0 0 4px ${p.couleur}55, 0 0 16px 4px ${p.couleur}99, 0 2px 8px rgba(0,0,0,0.25)`
                      : undefined,
                  }}
                  data-testid={`map-marker-${p.jourNumero}-${p.ordre}`}
                >
                  {p.ordre}
                </div>
              </Marker>
            );
          })}

          {/* Popup activité */}
          {activePoint && activePoint.coordinates && (
            <Popup
              longitude={activePoint.coordinates.lng}
              latitude={activePoint.coordinates.lat}
              anchor="bottom"
              offset={22}
              closeButton={false}
              closeOnClick={false}
              maxWidth="260px"
              className="voyage-map-popup"
              onClose={() => setActivePoint(null)}
            >
              <ActivityPopup
                point={activePoint}
                jourNumero={activePoint.jourNumero}
                couleur={activePoint.couleur}
                onClose={() => setActivePoint(null)}
              />
            </Popup>
          )}
        </Map>

        {/* Bouton recentrer */}
        <button
          onClick={fitToBounds}
          className="voyage-map-recenter"
          aria-label="Recentrer la carte"
          data-testid="map-recenter-btn"
        >
          <Crosshair size={18} strokeWidth={1.8} />
        </button>
      </div>

      {/* Bouton partager */}
      <div className="flex justify-center mt-5">
        <button
          onClick={handleShare}
          disabled={sharing}
          className="flex items-center justify-center gap-2 text-[#F9F6F0] py-3 rounded-md transition-all disabled:opacity-60"
          style={{ ...LORA, background: "#C17F59", width: "80%" }}
          data-testid="map-share-btn"
        >
          <Share2 size={16} strokeWidth={1.8} />
          {sharing ? "Préparation..." : "Partager ma carte 📸"}
        </button>
      </div>
    </div>
  );
};
